import { createAsyncThunk } from '@reduxjs/toolkit';
import { getErrorMessage } from '@/shared/utils/error';
import type { RootState } from '@/store';
import type { CertificadoGerado } from '../../types/arbitralDetailTypes';
import type {
  CertificadoAcessosArbitral,
  OrigemCertificadoAcessos,
  OrigemPdfCertificadoAcessos,
} from '../../types/certificadoAcessosTypes';
import { baixarPdfDoServidor } from '../../utils/pdfCertificadoDoServidor';
import { selectCertificadoAcessosPdfBaixando } from './selectors';
import { certificadosService } from './services';

export const fetchCertificados = createAsyncThunk<
  CertificadoGerado[],
  string,
  { rejectValue: string; state: RootState }
>('certificadosArbitrais/fetchCertificados', async (processoId, { rejectWithValue }) => {
  try {
    return await certificadosService.list(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const fetchCertificadoAcessos = createAsyncThunk<
  CertificadoAcessosArbitral,
  { processoId: string; origem: OrigemCertificadoAcessos },
  { rejectValue: string; state: RootState }
>(
  'certificadosArbitrais/fetchCertificadoAcessos',
  async ({ processoId, origem }, { rejectWithValue }) => {
    try {
      return await certificadosService.getCertificadoAcessos(processoId, origem);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const baixarPdfCertificadoAcessos = createAsyncThunk<
  void,
  { processoId: string; origem: OrigemPdfCertificadoAcessos; nomeArquivo: string },
  { rejectValue: string; state: RootState }
>(
  'certificadosArbitrais/baixarPdfCertificadoAcessos',
  async ({ processoId, origem, nomeArquivo }, { rejectWithValue, signal }) => {
    try {
      await baixarPdfDoServidor(
        (sinal) => certificadosService.solicitarPdfCertificadoAcessos(processoId, origem, sinal),
        nomeArquivo,
        signal,
      );
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Falha ao gerar o PDF'));
    }
  },
  {
    // Um clique é uma solicitação. Sem esta trava, o duplo clique (ou o re-render que reexecuta
    // o handler) abriria uma segunda cadeia de polling em paralelo, cada uma martelando o mesmo
    // endpoint até o teto.
    condition: (_arg, { getState }) => !selectCertificadoAcessosPdfBaixando(getState()),
  },
);

export const downloadCertificado = createAsyncThunk<
  string,
  { processoId: string; certificadoId: string },
  { rejectValue: string; state: RootState }
>(
  'certificadosArbitrais/downloadCertificado',
  async ({ processoId, certificadoId }, { rejectWithValue }) => {
    try {
      return await certificadosService.download(processoId, certificadoId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);
